import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useAddAlbumFormat } from "../../../../hooks/albumFormats/useAddAlbumFormat";
import { useModifyAlbumFormat } from "../../../../hooks/albumFormats/useModifyAlbumFormat";
import { useDeleteAlbumFormat } from "../../../../hooks/albumFormats/useDeleteAlbumFormat";
import { IAlbumFormat } from "../../../../hooks/albumFormats";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { Firebase } from "../../../../services/Firebase";
import { ProductPurchasesDialog } from "../../../products/ProductPurchasesDialog";
import { ProductType } from "../../../../hooks/products/interfaces";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";

export const ModifyAlbumFormatDialog: FunctionComponent<IModifyAlbumFormatDialogProps> = ({
  open,
  onClose,
  albumFormat,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = albumFormat ? strings.album_format_title : strings.maf_new_title;
  const { addAlbumFormat } = useAddAlbumFormat();
  const { modifyAlbumFormat } = useModifyAlbumFormat();
  const { deleteAlbumFormat } = useDeleteAlbumFormat();
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing_short),
    shortName: Yup.string().required(strings.field_missing_short)
  });
  const formik = useFormik({
    initialValues: {
      name: albumFormat?.name ?? "",
      shortName: albumFormat?.shortName ?? ""
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ name, shortName }) => {
      setIsLoading(true);
      if (albumFormat) {
        const { status, body } = await modifyAlbumFormat({
          uuid: albumFormat.uuid,
          name: name,
          shortName: shortName
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_format_action", { isNew: true });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addAlbumFormat({
          name: name,
          shortName: shortName
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_format_action", { isNew: false });
          onNewItem?.(body);
          onClose();
        } else if (StatusCodesHelper.isLimitReached(status)) {
          setAlertMessage(body.message);
          setIsPurchaseAlertOpen(true);
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!albumFormat) return;
    setIsLoading(true);
    const { status, body } = await deleteAlbumFormat({ uuid: albumFormat.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(albumFormat);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {albumFormat && (
            <ToolbarButton
              disabled={isLoading}
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.maf_name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.maf_name_placeholder}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
          <FieldTitle>{strings.maf_short_name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="shortName"
              name="shortName"
              size="small"
              placeholder={strings.maf_short_name_placeholder}
              value={formik.values.shortName}
              onChange={formik.handleChange}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} disabled={isLoading} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.ALBUM_FORMATS}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </FormikProvider>
  );
};

export interface IModifyAlbumFormatDialogProps {
  open: boolean;
  onClose: () => void;
  albumFormat?: IAlbumFormat;
  onNewItem?: (item: IAlbumFormat) => void;
  onModifiedItem?: (item: IAlbumFormat) => void;
  onDeletedItem?: (item: IAlbumFormat) => void;
}
